import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BRANCH_CODES, BRANCH_LABELS, type BranchCode, type Supplier } from '@lorsamthai/ops-shared';
import { createPurchaseOrder, listSuppliers, type CreatePOConflict } from '../lib/queries.js';
import { fmtInt, fmtDate, daysAgo } from '../lib/format.js';

interface Line {
  sku: string;
  qty: number;
  unit_cost?: number;
}

export default function PONew() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const destParam = sp.get('dest') as BranchCode | null;
  const skuParam = sp.get('sku') ?? '';
  const qtyParam = Number(sp.get('qty') ?? '4');
  const isValidBranch = (b: string | null): b is BranchCode =>
    !!b && BRANCH_CODES.includes(b as BranchCode);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [destBranch, setDestBranch] = useState<BranchCode>(
    isValidBranch(destParam) ? destParam : 'bp'
  );
  const [etaDate, setEtaDate] = useState<string>('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>([
    { sku: skuParam, qty: Number.isFinite(qtyParam) && qtyParam > 0 ? qtyParam : 4 },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<CreatePOConflict['warnings'] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listSuppliers().then((r) => setSuppliers(r.items)).catch(() => {});
  }, []);

  const setLine = (i: number, patch: Partial<Line>) => {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, { sku: '', qty: 4 }]);
  const delLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e: FormEvent, force = false) => {
    e.preventDefault();
    setError(null);
    if (!supplierId) {
      setError('เลือก supplier ก่อน');
      return;
    }
    const items = lines.filter((l) => l.sku.trim() && l.qty > 0);
    if (items.length === 0) {
      setError('ต้องมีอย่างน้อย 1 รายการ');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createPurchaseOrder({
        supplier_id: supplierId as number,
        dest_branch: destBranch,
        items: items.map((l) => ({ sku: l.sku.trim(), qty: l.qty, unit_cost: l.unit_cost })),
        eta_date_promised: etaDate || undefined,
        note: note || undefined,
        force,
        mark_ordered: true,
      });
      if ('warnings' in res) {
        setWarnings(res.warnings);
      } else {
        navigate(`/pos/${res.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Link to="/pos" className="text-xs text-slate-500 hover:underline">← กลับรายการ PO</Link>
        <h1 className="mt-1 text-xl font-bold">สั่งซื้อยางเข้า</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="block font-medium">Supplier</span>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                <option value="">— เลือก —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.reliability !== 'normal' && ` (${s.reliability})`}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-sm">
              <span className="block font-medium">สาขาปลายทาง</span>
              <select
                value={destBranch}
                onChange={(e) => setDestBranch(e.target.value as BranchCode)}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5"
              >
                {BRANCH_CODES.map((b) => (
                  <option key={b} value={b}>
                    {BRANCH_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-sm">
              <span className="block font-medium">ETA ที่ supplier สัญญา (ไม่ต้องใส่ก็ได้)</span>
              <input
                type="date"
                value={etaDate}
                onChange={(e) => setEtaDate(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-1.5"
              />
            </label>

            <label className="space-y-1 text-sm">
              <span className="block font-medium">หมายเหตุ</span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="เช่น lot ผลิตสัปดาห์หน้า"
                className="w-full rounded-md border border-slate-200 px-3 py-1.5"
              />
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">รายการ SKU</h2>
            <button
              type="button"
              onClick={addLine}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50"
            >
              + เพิ่มรายการ
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={l.sku}
                  onChange={(e) => setLine(i, { sku: e.target.value })}
                  placeholder="SKU"
                  className="flex-1 rounded-md border border-slate-200 px-2 py-1 font-mono text-xs"
                />
                <input
                  type="number"
                  min={1}
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: Number(e.target.value) })}
                  className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right"
                />
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={l.unit_cost ?? ''}
                  onChange={(e) => setLine(i, { unit_cost: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="ราคา/เส้น"
                  className="w-28 rounded-md border border-slate-200 px-2 py-1 text-right text-xs"
                />
                <button
                  type="button"
                  onClick={() => delLine(i)}
                  className="text-xs text-slate-400 hover:text-red-500"
                  disabled={lines.length === 1}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {warnings && warnings.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
            <h3 className="text-sm font-semibold text-amber-900">
              ⚠️ มี PO เดิมรออยู่ — ยืนยันจะสั่งซ้ำหรือไม่?
            </h3>
            <ul className="mt-2 space-y-1 text-sm text-amber-900">
              {warnings.map((w, i) => {
                const age = daysAgo(w.ordered_at);
                return (
                  <li key={i} className="rounded-md bg-white px-3 py-2">
                    <span className="font-mono text-xs">{w.sku}</span>
                    <span className="ml-2 text-xs">
                      รออยู่ {fmtInt(w.pending_qty)} เส้น · PO {w.po_ref ?? `#${w.po_id}`}
                      {age !== null && ` · ${age} วันก่อน`}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setWarnings(null)}
                className="rounded-md border border-amber-300 px-3 py-1.5 text-sm"
              >
                ยกเลิก ปรับจำนวนก่อน
              </button>
              <button
                type="button"
                onClick={(e) => void submit(e, true)}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white"
              >
                ยืนยัน สั่งเพิ่มเลย
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? 'กำลังบันทึก...' : 'บันทึกคำสั่งซื้อ'}
          </button>
        </div>
      </form>
    </div>
  );
}
